/**
 * Helpers for the PandaCore engine comparisons shown on /pandacore.
 *
 * The dataset itself lives in pandaCoreTunBenchResults.ts, generated from SagerNet tun-bench result
 * files by scripts/pandacore-bench/generate-tunbench-results.mjs. Nothing here is synthesised; every
 * number on the page is derived from measured cells.
 */

export interface PairMetric {
  id: string
  pandaCore: number
  singBox: number
  unit: string
  higherIsBetter: boolean
  /** Marked when the two medians sit inside the tie band, i.e. neither can claim the cell. */
  tie?: boolean
  /** A further engine's measured value. */
  engine2?: number
}

export interface MetricComparison {
  rival: 'singBox' | 'engine2'
  value: number
  multiplier: number
  deltaPercent: number
  leads: boolean
  tie: boolean
}

/** Keep each value attached to its rival; sort exact advantages before formatting for display. */
export function rankComparisons(metric: PairMetric): [MetricComparison, ...MetricComparison[]] {
  const compare = (rival: MetricComparison['rival'], value: number): MetricComparison => {
    if (!Number.isFinite(metric.pandaCore) || metric.pandaCore <= 0 || !Number.isFinite(value) || value <= 0) {
      throw new Error(`Benchmark metric ${metric.id} needs positive finite measurements for ${rival}`)
    }
    const pair = { ...metric, singBox: value }
    const verdict = verdictFromMedians(metric.pandaCore, value, metric.higherIsBetter)
    return {
      rival,
      value,
      multiplier: advantageMultiplier(pair),
      deltaPercent: customerDelta(pair),
      leads: verdict === 'pandaCore',
      tie: verdict === 'tie',
    }
  }
  const comparisons: [MetricComparison, ...MetricComparison[]] = [compare('singBox', metric.singBox)]
  if (metric.engine2 !== undefined) {
    comparisons.push(compare('engine2', metric.engine2))
  }
  return comparisons.sort((a, b) => b.multiplier - a.multiplier)
}

export type Verdict = 'pandaCore' | 'singBox' | 'tie'

export interface Tally {
  wins: number
  ties: number
  losses: number
}

function assertMeasured(metric: PairMetric) {
  if (!(metric.pandaCore > 0) || !(metric.singBox > 0)) {
    throw new Error(`Benchmark metric ${metric.id} must have positive measurements on both sides`)
  }
}

/** How many times better PandaCore is. >1 means PandaCore leads regardless of metric direction. */
export function advantageMultiplier(metric: PairMetric): number {
  assertMeasured(metric)
  return metric.higherIsBetter
    ? metric.pandaCore / metric.singBox
    : metric.singBox / metric.pandaCore
}

/**
 * Signed percentage as a customer reads it. Higher-is-better: how much more PandaCore
 * delivers (+44). Lower-is-better: how much less PandaCore uses (-57).
 */
export function customerDelta(metric: PairMetric): number {
  assertMeasured(metric)
  return (metric.pandaCore / metric.singBox - 1) * 100
}

/** Medians closer than this (in percent) are a tie; the page and the raw table both use it. */
export const TIE_BAND_PERCENT = 5

/**
 * Verdict from the two medians: a cell is a tie when they differ by less than TIE_BAND_PERCENT,
 * otherwise the better median leads. Symmetric, so 0.97x and 1.03x are both ties.
 */
export function verdictFromMedians(pandaMedian: number, singMedian: number, higherIsBetter: boolean, tieBandPercent = TIE_BAND_PERCENT): Verdict {
  if (!(pandaMedian > 0) || !(singMedian > 0)) {
    throw new Error('A verdict needs positive medians on both sides')
  }
  const deltaPercent = (pandaMedian / singMedian - 1) * 100
  if (Math.abs(deltaPercent) < tieBandPercent) {
    return 'tie'
  }
  const pandaBetter = higherIsBetter ? deltaPercent > 0 : deltaPercent < 0
  return pandaBetter ? 'pandaCore' : 'singBox'
}

export function pandaCoreLeads(metric: PairMetric): boolean {
  assertMeasured(metric)
  if (metric.tie) {
    return false
  }
  return advantageMultiplier(metric) > 1
}

export function tallyVerdicts(verdicts: readonly Verdict[]): Tally {
  const result: Tally = { wins: 0, ties: 0, losses: 0 }
  for (const verdict of verdicts) {
    if (verdict === 'pandaCore') {
      result.wins += 1
    }
    else if (verdict === 'singBox') {
      result.losses += 1
    }
    else {
      result.ties += 1
    }
  }
  return result
}

/** "4.3×" style label. One decimal below 100, integer from 100 up. */
export function formatMultiplier(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`Cannot format multiplier ${value}`)
  }
  const digits = value >= 100 ? 0 : 1
  return `${value.toFixed(digits)}×`
}

/** "−65%" / "+17%" with a real minus sign, rounded to whole percent. */
export function formatSignedPercent(value: number): string {
  if (!Number.isFinite(value)) {
    throw new Error(`Cannot format percent ${value}`)
  }
  const rounded = Math.round(value)
  if (rounded === 0) {
    return '0%'
  }
  return rounded > 0 ? `+${rounded}%` : `−${Math.abs(rounded)}%`
}

/** CPU spent per Gbit/s moved: the honest way to read the load numbers. */
export function cpuPercentPerGbit(cpuPercent: number, gbitPerSecond: number): number {
  if (!(cpuPercent > 0) || !(gbitPerSecond > 0)) {
    throw new Error('CPU efficiency needs positive load and throughput')
  }
  return cpuPercent / gbitPerSecond
}

/** Gigabytes moved in `seconds` at a sustained Gbit/s rate. */
export function gigabytesTransferred(gbitPerSecond: number, seconds: number): number {
  if (!(gbitPerSecond >= 0) || !(seconds >= 0)) {
    throw new Error('Transfer needs non-negative rate and duration')
  }
  return (gbitPerSecond * seconds) / 8
}

// ---------------------------------------------------------------------------------------------
// tun-bench dataset (SagerNet/tun-bench, the methodology behind the sing-tun new-stack write-up).
// Generated into pandaCoreTunBenchResults.ts by scripts/pandacore-bench/generate-tunbench-results.mjs.
// ---------------------------------------------------------------------------------------------

export type TunBenchSide = 'pandaCore' | 'singBox' | 'engine2'

export interface TunBenchThroughputSample {
  gbitPerSecond: number
  /** Average engine-process CPU over the cell, in percent of one core. */
  cpuPercent: number
  cpuPercentPerGbit: number
  /** Only on platforms where tun-bench reads energy (macOS): tunnel-process joules per Gbit moved. */
  joulesPerGbit?: number
  /** TUN queues the engine ran with (tun-bench turns on sing-box multi_queue for parallel > 1). */
  queues: number
  seconds: number
}

export interface TunBenchThroughputCell {
  /** `upload-p1`, `download-p8`, `upload-p1-1gbps`, … */
  id: string
  direction: 'upload' | 'download'
  parallel: number
  /** 0 means unlimited; otherwise every iperf3 stream is capped at this rate. */
  bitrateGbps: number
  unit: 'Gbit/s'
  higherIsBetter: true
  pandaCore: TunBenchThroughputSample
  singBox: TunBenchThroughputSample
  engine2?: TunBenchThroughputSample
}

export interface TunBenchMemoryPoint {
  connections: number
  mib: number
}

export interface TunBenchMemorySeries {
  points: TunBenchMemoryPoint[]
  idleMiB: number
  loadedMiB: number
  slopeMiBPer100: number
}

export interface TunBenchMemoryCell {
  id: 'memory'
  metric?: string
  maxConnections: number
  connectionStep: number
  payloadBytes: number
  pandaCore: TunBenchMemorySeries
  singBox: TunBenchMemorySeries
  engine2?: TunBenchMemorySeries
}

export interface TunBenchPlatform {
  id: 'macos' | 'linux'
  os: string
  arch: string
  host: string
  measuredAt: string
  /** Number of complete measurements per cell, aggregated independently by median. */
  rounds?: number
  mtu: number
  durationSeconds: number
  warmupSeconds: number
  tunnelWorkers: number
  loadMetric: 'cpu' | 'energy'
  pandaCoreVersion: string
  pandaCoreBinarySha256?: string
  singBoxVersion: string
  engine2?: { version: string, stack: string }
  throughput: TunBenchThroughputCell[]
  memory: TunBenchMemoryCell
}

export interface TunBenchDataset {
  tool: { name: 'tun-bench', commit: string }
  generatedFrom: string[]
  platforms: TunBenchPlatform[]
}

export function findPlatform(dataset: TunBenchDataset, id: TunBenchPlatform['id']): TunBenchPlatform {
  const platform = dataset.platforms.find(item => item.id === id)
  if (!platform) {
    throw new Error(`Missing benchmark platform ${id}`)
  }
  return platform
}

export function findThroughput(platform: TunBenchPlatform, direction: TunBenchThroughputCell['direction'], parallel: number, bitrateGbps = 0): TunBenchThroughputCell {
  const cell = platform.throughput.find(item => item.direction === direction && item.parallel === parallel && item.bitrateGbps === bitrateGbps)
  if (!cell) {
    throw new Error(`Missing benchmark cell ${direction} p${parallel} at ${bitrateGbps || 'unlimited'} Gbit/s on ${platform.id}`)
  }
  return cell
}

/** Throughput as a pair metric (higher is better), with the tie flag from the two medians. */
export function throughputMetric(cell: TunBenchThroughputCell): PairMetric {
  return {
    id: cell.id,
    pandaCore: cell.pandaCore.gbitPerSecond,
    singBox: cell.singBox.gbitPerSecond,
    unit: cell.unit,
    higherIsBetter: true,
    tie: verdictFromMedians(cell.pandaCore.gbitPerSecond, cell.singBox.gbitPerSecond, true) === 'tie',
    ...(cell.engine2 ? { engine2: cell.engine2.gbitPerSecond } : {}),
  }
}

/** CPU per Gbit/s moved in a cell (lower is better): the load comparison that survives unequal throughput. */
export function cpuEfficiencyMetric(cell: TunBenchThroughputCell): PairMetric {
  return {
    id: `cpu-${cell.id}`,
    pandaCore: cell.pandaCore.cpuPercentPerGbit,
    singBox: cell.singBox.cpuPercentPerGbit,
    unit: '%/Gbit',
    higherIsBetter: false,
    tie: verdictFromMedians(cell.pandaCore.cpuPercentPerGbit, cell.singBox.cpuPercentPerGbit, false) === 'tie',
    ...(cell.engine2 ? { engine2: cell.engine2.cpuPercentPerGbit } : {}),
  }
}

/** Process CPU-energy estimate per transferred Gbit; not whole-device power. */
export function energyMetric(cell: TunBenchThroughputCell): PairMetric {
  const joules = (sample: TunBenchThroughputSample): number => {
    if (sample.joulesPerGbit === undefined || !(sample.joulesPerGbit > 0)) {
      throw new Error(`Cell ${cell.id} has no positive energy sample`)
    }
    return sample.joulesPerGbit
  }
  return {
    id: `energy-${cell.id}`,
    pandaCore: joules(cell.pandaCore),
    singBox: joules(cell.singBox),
    ...(cell.engine2 ? { engine2: joules(cell.engine2) } : {}),
    unit: 'J/Gbit',
    higherIsBetter: false,
  }
}

/** Every measured public-matrix metric, including capped cells and intermediate memory points. */
export function platformMetrics(platform: TunBenchPlatform): PairMetric[] {
  return [
    ...platform.throughput.map(throughputMetric),
    ...platform.throughput.map(cpuEfficiencyMetric),
    ...(platform.loadMetric === 'energy' ? platform.throughput.map(energyMetric) : []),
    ...platform.memory.pandaCore.points.map(point => memoryMetric(platform.memory, point.connections)),
  ]
}

export function platformTally(platform: TunBenchPlatform): Tally {
  return tallyVerdicts(platformMetrics(platform).flatMap(metric => rankComparisons(metric)
    .map(comparison => comparison.tie ? 'tie' : comparison.leads ? 'pandaCore' : 'singBox')))
}

/** Resident memory with `connections` idle connections open (lower is better). */
export function memoryMetric(cell: TunBenchMemoryCell, connections: number): PairMetric {
  const at = (series: TunBenchMemorySeries, side: string): number => {
    const point = series.points.find(item => item.connections === connections)
    if (!point) {
      throw new Error(`Memory series for ${side} has no point at ${connections} connections`)
    }
    return point.mib
  }
  const pandaCore = at(cell.pandaCore, 'pandaCore')
  const singBox = at(cell.singBox, 'singBox')
  return {
    id: `memory-${connections}`,
    pandaCore,
    singBox,
    unit: 'MiB',
    higherIsBetter: false,
    tie: verdictFromMedians(pandaCore, singBox, false) === 'tie',
    ...(cell.engine2 ? { engine2: at(cell.engine2, 'engine2') } : {}),
  }
}
